import { createApiRef } from '@backstage/core-plugin-api';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type ApprovalState = 'pending' | 'success' | 'failure' | 'none';

export interface EnvironmentCommitRef {
  sha: string;
  url: string;
  /**
   * The `Promoter-Source-Sha` cicd-promoter stamps into the promotion PR
   * body that put this commit here — the original commit from `main` the
   * whole promotion chain is hydrated from. Undefined if no promotion PR
   * (merged or open) could be found for this ref.
   */
  sourceSha?: string;
}

export interface ProposedPromotion extends EnvironmentCommitRef {
  prNumber: number;
  prUrl: string;
  compareUrl: string;
}

export interface CommitStatusSummary {
  context: string;
  state: 'success' | 'failure' | 'error' | 'pending';
  description?: string;
  targetUrl?: string;
}

export interface EnvironmentStatus {
  env: string;
  current: EnvironmentCommitRef;
  proposed: ProposedPromotion | null;
  approval: {
    required: boolean;
    state: ApprovalState;
  };
  /** Upstream environment names this env promotes from. Empty for a root env (e.g. "dev"). */
  dependsOn: string[];
  /** All GitHub commit statuses on current.sha (not just the promoter/approval one used for the approval chip). */
  statuses: CommitStatusSummary[];
}

export interface StatusResponse {
  enabled: boolean;
  environments: EnvironmentStatus[];
}

export interface CicdPromoterApi {
  getStatus(owner: string, repo: string): Promise<StatusResponse>;
}

export const cicdPromoterApiRef = createApiRef<CicdPromoterApi>({
  id: 'plugin.cicd-promoter',
});

export class CicdPromoterApiClient implements CicdPromoterApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('cicd-promoter');
  }

  async getStatus(owner: string, repo: string): Promise<StatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/status?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ??
          `Failed to fetch cicd-promoter status: ${response.status}`,
      );
    }
    return response.json();
  }
}
